import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchIcon, XIcon, ClockIcon, FileTextIcon, UsersIcon, CreditCardIcon } from 'lucide-react';
import { getTenant } from '../../theme/tokens';
import { useNavigate } from 'react-router-dom';
interface SearchResult {
  id: string;
  type: 'transaction' | 'customer' | 'invoice' | 'dispute';
  title: string;
  subtitle: string;
  link: string;
}
export function UniversalSearchBar() {
  const {
    t
  } = useTranslation('common');
  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const listboxId = 'universal-search-listbox';
  const inputId = 'universal-search-input';
  const optionId = (i: number) => `universal-search-option-${i}`;
  const mockResults: SearchResult[] = [{
    id: '1',
    type: 'transaction',
    title: 'TXN-2024-0100',
    subtitle: `${getTenant().currency.code} 100.01 • 1 Dec 2024`,
    link: '/sales/1'
  }, {
    id: '2',
    type: 'customer',
    title: 'John Doe',
    subtitle: 'customer@example.com',
    link: '/customers/1'
  }, {
    id: '3',
    type: 'invoice',
    title: 'INV-2024-001',
    subtitle: `${getTenant().currency.code} 1,500.00 • Due 15 Dec 2024`,
    link: '/invoices/1'
  }, {
    id: '4',
    type: 'dispute',
    title: 'DP-2024-0001',
    subtitle: 'Fraudulent • Requires response',
    link: '/disputes/1'
  }];
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setActiveIndex(-1);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  useEffect(() => {
    if (query.length > 0) {
      const filtered = mockResults.filter(result => result.title.toLowerCase().includes(query.toLowerCase()) || result.subtitle.toLowerCase().includes(query.toLowerCase()));
      setResults(filtered);
      setIsOpen(true);
      setActiveIndex(filtered.length > 0 ? 0 : -1);
    } else {
      setResults([]);
      setIsOpen(false);
      setActiveIndex(-1);
    }
  }, [query]);
  const handleResultClick = (link: string) => {
    navigate(link);
    setQuery('');
    setIsOpen(false);
    setActiveIndex(-1);
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || results.length === 0) {
      if (e.key === 'Escape') {
        setQuery('');
        setIsOpen(false);
      }
      return;
    }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex(i => (i + 1) % results.length);
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex(i => (i - 1 + results.length) % results.length);
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(0);
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(results.length - 1);
        break;
      case 'Enter':
        if (activeIndex >= 0 && activeIndex < results.length) {
          e.preventDefault();
          handleResultClick(results[activeIndex].link);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIsOpen(false);
        setActiveIndex(-1);
        break;
    }
  };
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'transaction':
        return <CreditCardIcon size={16} className="text-blue-600" aria-hidden="true" data-id="element-15847" />;
      case 'customer':
        return <UsersIcon size={16} className="text-green-600" aria-hidden="true" data-id="element-15848" />;
      case 'invoice':
        return <FileTextIcon size={16} className="text-purple-600" aria-hidden="true" data-id="element-15849" />;
      case 'dispute':
        return <ClockIcon size={16} className="text-red-600" aria-hidden="true" data-id="element-15850" />;
      default:
        return <SearchIcon size={16} className="text-gray-600" aria-hidden="true" data-id="element-15851" />;
    }
  };
  const placeholder = t('ui.UniversalSearchBar.attrs.search_transactions_customers_invoices') as string;
  return <div ref={searchRef} className="relative w-full max-w-2xl" data-id="element-15852">
      <div className="relative" data-id="element-15853">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-15854">
          <SearchIcon size={18} className="text-gray-400" aria-hidden="true" data-id="element-15855" />
        </div>
        <input id={inputId} ref={inputRef} type="text" role="combobox" aria-expanded={isOpen && results.length > 0} aria-controls={listboxId} aria-autocomplete="list" aria-activedescendant={activeIndex >= 0 ? optionId(activeIndex) : undefined} aria-label={placeholder} value={query} onChange={e => setQuery(e.target.value)} onKeyDown={handleKeyDown} placeholder={placeholder} className="block w-full pl-10 pr-10 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors" data-id="element-15856" />
        {query && <button type="button" onClick={() => {
        setQuery('');
        setIsOpen(false);
        setActiveIndex(-1);
        inputRef.current?.focus();
      }} aria-label={t('actions.clear') as string || 'Clear search'} className="absolute inset-y-0 right-0 pr-3 flex items-center rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500" data-id="element-15857">
            <XIcon size={18} className="text-gray-400 hover:text-gray-600" aria-hidden="true" data-id="element-15858" />
          </button>}
      </div>

      {/* Live region for result count */}
      <div className="sr-only" aria-live="polite" aria-atomic="true" data-id="element-15859">
        {isOpen && query.length > 0 ? results.length === 0 ? `No results found for ${query}` : `${results.length} results available` : ''}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && results.length > 0 && <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto" data-id="element-15860">
          <div className="p-2" data-id="element-15861">
            <div className="text-xs font-medium text-gray-500 px-3 py-2" id="universal-search-label" data-id="element-15862">
              {t('ui.UniversalSearchBar.search_results')}
              {results.length})
            </div>
            <ul id={listboxId} role="listbox" aria-labelledby="universal-search-label" className="list-none p-0 m-0" data-id="element-15863">
              {results.map((result, i) => <li key={result.id} id={optionId(i)} role="option" aria-selected={i === activeIndex} data-id="element-15864">
                  <button type="button" onClick={() => handleResultClick(result.link)} onMouseEnter={() => setActiveIndex(i)} className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors text-left ${i === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-50'}`} tabIndex={-1} data-id="element-15865">
                    <div className="flex-shrink-0" data-id="element-15866">
                      {getTypeIcon(result.type)}
                    </div>
                    <div className="flex-1 text-left" data-id="element-15867">
                      <div className="text-sm font-medium text-gray-900" data-id="element-15868">
                        {result.title}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-15869">
                        {result.subtitle}
                      </div>
                    </div>
                    <div className="text-xs text-gray-400 capitalize" data-id="element-15870">
                      {result.type}
                    </div>
                  </button>
                </li>)}
            </ul>
          </div>
        </div>}

      {/* No Results */}
      {isOpen && query.length > 0 && results.length === 0 && <div className="absolute z-50 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg p-4" data-id="element-15871">
          <div className="text-center text-sm text-gray-500" data-id="element-15872">
            {t('ui.UniversalSearchBar.no_results_found_for')}
            {query}"
          </div>
        </div>}
    </div>;
}